import React, { useContext, useState, useEffect } from 'react';

import { GeneralInput, DetailInput, PlanTile } from './components';
import { AuthContext } from 'context/AuthContext';
import { Page, PageHeader, SearchBar } from 'components';
import AddIcon from '@material-ui/icons/Add';
import {
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  Slide,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ImageList,
  ImageListItem,
} from '@mui/material';

import { useProperties, useEvents, useOrganizations } from 'hooks';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { green, grey } from '@mui/material/colors';
import SimpleTable from 'components/table/SimpleTable';
import { DashboardHelpers } from 'utils';

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PlansPage = () => {
  const { selectedPropertyId } = useProperties();
  const { selectedOrganizationId } = useOrganizations();
  const { createEvent, farmerPlan, setPlan, clearPlan, inputValidities, events, updateEvent } = useEvents();
  const auth = useContext(AuthContext);
  const [displayDetails, setDisplayDetails] = useState('none');
  const [submitting, setSubmitting] = useState(false);
  const [showPlanningForm, setShowPlanningForm] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();

  const tableHeaders = [
    {
      alias: 'id',
      sortable: false,
      format: 'string',
      dataAlias: 'id',
      noDisplay: true,
    },
    {
      alias: 'time',
      sortable: true,
      format: 'string',
      dataAlias: 'time',
      dataRender: (r) => {
        if (r.time) {
          return DashboardHelpers.formatDateTime(r.time);
        }
      },
    },
    {
      alias: 'type',
      sortable: true,
      format: 'string',
      dataAlias: 'plan_type',
      dataRender: (r) => {
        if (r.data && r.data.plan_type) {
          return r.data.plan_type;
        } else {
          return t('none_specified');
        }
      },
    },
  ];

  const handleSearch = (text) => {
    if (text !== searchText) {
      setSearchText(text);
      search(text);
    }
  };

  let filtered = [];
  _.forEach(events, (eventBucket) => {
    const evBucket = _.filter(eventBucket, (event) => {
      return event.type === 'plan';
    });

    filtered = filtered.concat(...evBucket);
  });

  const filterAndFlatten = (text) => {
    return _.filter(filtered, (val) => {
      return DashboardHelpers.searchObject(val, text, tableHeaders);
    });
  };

  const search = (text) => {
    filtered = filterAndFlatten(text);
  };

  useEffect(() => {
    setSearchText('');
  }, [selectedOrganizationId]);

  const handleToggleShowPlanningForm = () => {
    setShowPlanningForm(!showPlanningForm);
  };

  const handleClose = () => {
    setShowPlanningForm(false);
    clearPlan();
  };

  const handleSelectAndShow = (plan) => {
    setShowPlanningForm(true);
    setPlan(plan);
  };

  const pageProps = {};
  pageProps.actionFab = true;
  pageProps.actionHref = () => {
    handleToggleShowPlanningForm();
  };
  pageProps.action = 'plans_add';
  pageProps.icon = <AddIcon />;

  let upcomingEvents = _.filter(filtered, (event) => {
    return new Date(event.time) > new Date();
  });

  upcomingEvents = _.orderBy(upcomingEvents, ['time'], ['asc']).slice(0, 5);

  const handleGenDone = (done) => {
    if (done) {
      setDisplayDetails('');
    } else {
      setDisplayDetails('none');
    }
  };

  return (
    <Page>
      <Dialog
        open={showPlanningForm}
        onClose={handleClose}
        scroll="paper"
        TransitionComponent={Transition}
        aria-labelledby="scroll-dialog-title"
      >
        <DialogTitle
          sx={{
            backgroundColor: grey[300],
          }}
        >
          <Typography sx={{ color: grey[700] }}>{t('plan_creation')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container direction="row" paddingTop={2}>
            <GeneralInput key={selectedPropertyId} done={handleGenDone} />
            <DetailInput sx={{ display: displayDetails }} key={selectedPropertyId + '_details'} />
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: grey[300],
            borderBottomColor: grey[400],
            borderBottomWidth: 1,
            borderBottomStyle: 'groove',
          }}
        >
          {!submitting ? (
            <Button
              disabled={!inputValidities.generalValid || !inputValidities.specialValid}
              onClick={() => {
                setSubmitting(true);
                if (farmerPlan.id) {
                  updateEvent(
                    Object.assign({}, farmerPlan, {
                      layer: 'field',
                      organization_id: selectedOrganizationId,
                      created_by: auth.user && auth.user.display_name ? auth.user.display_name : auth.user.login,
                    })
                  ).then(() => {
                    setSubmitting(false);
                    handleToggleShowPlanningForm();
                    clearPlan();
                  });
                } else {
                  createEvent(
                    Object.assign({}, farmerPlan, {
                      layer: 'field',
                      organization_id: selectedOrganizationId,
                      created_by: auth.user && auth.user.display_name ? auth.user.display_name : auth.user.login,
                    })
                  ).then(() => {
                    setSubmitting(false);
                    handleToggleShowPlanningForm();
                    clearPlan();
                  });
                }
              }}
            >
              {t('save')}
            </Button>
          ) : (
            <CircularProgress />
          )}
          <Button
            onClick={() => {
              clearPlan();
              handleToggleShowPlanningForm();
            }}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <PageHeader title="plan_creation" {...pageProps}></PageHeader>
      <Card>
        <CardContent>
          <Typography variant="h5">{t('plans_upcoming')}</Typography>
          {upcomingEvents.length === 0 && (
            <Typography variant="body1" sx={{ margin: 5 }}>
              {t('plans_no_upcoming')},{' '}
              <a href="#" onClick={handleToggleShowPlanningForm} style={{ color: green[200], cursor: 'pointer' }}>
                {t('add_one_prompt')}
              </a>
            </Typography>
          )}
          <ImageList
            sx={{
              gridAutoFlow: 'column',
              gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr)) !important',
              gridAutoColumns: 'minmax(160px, 1fr)',
            }}
          >
            {_.map(upcomingEvents, (event) => (
              <ImageListItem key={event.id} padding={2}>
                <PlanTile
                  plan={event}
                  onClick={() => {
                    handleSelectAndShow(event);
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        </CardContent>
      </Card>
      <Grid marginTop={2}>
        <SearchBar setSearchText={handleSearch} selector={searchText} />
      </Grid>
      <Grid marginTop={2}>
        <SimpleTable
          title={t('plans')}
          data={filtered}
          tableHeader={tableHeaders}
          selectRow={(e, d) => handleSelectAndShow(d)}
        />
      </Grid>
    </Page>
  );
};

export default PlansPage;
